import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Network, ListResult } from 'common/api/v1/types'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { withDefaultPagination } from '../../utils'
import { ApplicationException } from '../../api/ApplicationException'
import { NetworksRequestParams } from '../../api/nm-types'
import type { ThunkApi } from '../../store'

export const createNetwork = createAsyncThunk<void, Omit<Network, 'id'>, ThunkApi>(
  'networks/createNetwork',
  async (network, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      await api.networksApi.createNetwork(network)
      navigate()(routes.networks())
      dispatch(enqueueSuccessSnackbar(`Created Network ${network.name}`))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'create network' }))
      if (err instanceof ApplicationException) return rejectWithValue(err.errorInfo)
      throw err
    }
  },
)

export const removeNetwork = createAsyncThunk<{ id: Network['id']; name: Network['name'] }, Network['id'], ThunkApi>(
  'networks/removeNetwork',
  async (id, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.networksApi.removeNetwork(id)
      navigate()(routes.networks())
      dispatch(enqueueSuccessSnackbar(`Removed Network: ${res.name}`))
      return res
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete network' }))
      throw err
    }
  },
)

export const updateNetwork = createAsyncThunk<void, Network, ThunkApi>(
  'networks/updateNetwork',
  async (network, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      await api.networksApi.updateNetwork(network.id, {
        name: network.name,
        enabled: network.enabled,
      })
      navigate()(routes.networks())
      dispatch(enqueueSuccessSnackbar(`Updated Network: ${network.name}`))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'update network' }))
      if (err instanceof ApplicationException) return rejectWithValue(err.errorInfo)
      throw err
    }
  },
)

export const getNetworks = createAsyncThunk<ListResult<Network>, Partial<NetworksRequestParams>, ThunkApi>(
  'networks/getNetworks',
  async (params, { extra: { api } }) => {
    return await api.networksApi.getNetworks(withDefaultPagination(params))
  },
)

export const getNetwork = createAsyncThunk<Network, Network['id'], ThunkApi>(
  'networks/getNetwork',
  async (id, { extra: { api } }) => {
    return await api.networksApi.getNetwork(id)
  },
)

export const clearNetwork = createAction('networks/clearNetwork')
export const clearNetworks = createAction('networks/clearNetworks')
